import { useEffect, useState, useCallback } from "react";
import api, { formatUSD } from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, FileText, Filter, ChevronLeft, ChevronRight, Eye, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_FLOW = ["pending", "awaiting_acceptance", "paid", "preparing", "delivering", "delivered"];
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  awaiting_acceptance: "bg-orange-100 text-orange-800 animate-pulse",
  paid: "bg-blue-100 text-blue-800",
  preparing: "bg-purple-100 text-purple-800",
  delivering: "bg-indigo-100 text-indigo-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

const pickupBadge = (dm) => {
  const isFoodTruck = dm === "pickup_foodtruck";
  return isFoodTruck
    ? { label: "FOOD TRUCK", emoji: "🚚", cls: "bg-[var(--dd-coral)] text-white", rowAccent: "border-l-4 border-l-[var(--dd-coral)]" }
    : { label: "RESTAURANT", emoji: "🏪", cls: "bg-[var(--dd-green)] text-white", rowAccent: "border-l-4 border-l-[var(--dd-green)]" };
};

const PAGE_SIZE = 20;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalAll, setTotalAll] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await api.get("/admin/orders", { params });
      setOrders(res.data.items);
      setTotal(res.data.total);
      setStatusCounts(res.data.status_counts || {});
      setTotalAll(res.data.total_all || 0);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [q, status, fromDate, toDate, skip]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      load();
      if (selected?.id === id) setSelected({ ...selected, status: newStatus });
    } catch { toast.error("Update failed"); }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    const token = localStorage.getItem("dsf_admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/export.csv?${params.toString()}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const openInvoice = (orderId) => {
    const token = localStorage.getItem("dsf_admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${orderId}/invoice?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-bold text-3xl text-[var(--dd-green)]">Orders</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">{totalAll} total · {total} matching filters</p>
        </div>
        <button onClick={exportCsv} className="btn-mustard !py-2.5" data-testid="orders-export-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="orders-status-tabs">
        <button
          onClick={() => { setStatus("all"); setSkip(0); }}
          className={`px-4 py-2 rounded-full text-xs font-semibold transition ${status === "all" ? "bg-[var(--dd-green)] text-white" : "bg-[var(--dd-bone)] text-[var(--dd-green)] hover:bg-[var(--dd-green-soft)]"}`}
          data-testid="status-tab-all"
        >
          All ({totalAll})
        </button>
        {[...STATUS_FLOW, "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => { setStatus(s); setSkip(0); }}
            className={`px-4 py-2 rounded-full text-xs font-semibold capitalize transition ${status === s ? "bg-[var(--dd-green)] text-white" : "bg-[var(--dd-bone)] text-[var(--dd-green)] hover:bg-[var(--dd-green-soft)]"}`}
            data-testid={`status-tab-${s}`}
          >
            {s} ({statusCounts[s] || 0})
          </button>
        ))}
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-2xl border border-black/5 p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="orders-filters">
        <div className="md:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--dsf-text-muted)]" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setSkip(0); }}
            placeholder="Search by order #, email, name, phone…"
            className="pl-10"
            data-testid="orders-search-input"
          />
        </div>
        <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setSkip(0); }} placeholder="From" data-testid="orders-from-date" />
        <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setSkip(0); }} placeholder="To" data-testid="orders-to-date" />
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-[var(--dd-bone)]">
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[var(--dd-green)] font-bold">
                <th className="px-4 py-3">Order #</th>
                <th className="px-4 py-3">Pickup</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="text-center py-12 text-[var(--dsf-text-muted)]">Loading…</td></tr>}
              {!loading && orders.length === 0 && (
                <tr><td colSpan="8" className="text-center py-12 text-[var(--dsf-text-muted)]">No orders found</td></tr>
              )}
              {orders.map((o) => {
                const pb = pickupBadge(o.delivery_method);
                return (
                <tr key={o.id} className={`border-t border-black/5 hover:bg-[var(--dd-bone)]/40 ${pb.rowAccent}`} data-testid={`order-row-${o.order_number}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-[var(--dd-green)]">{o.order_number}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider ${pb.cls}`} data-testid={`order-pickup-${o.order_number}`}>
                      <span>{pb.emoji}</span> {pb.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--dsf-text)]">{o.customer_name}</div>
                    <div className="text-xs text-[var(--dsf-text-muted)]">{o.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-[var(--dsf-text-secondary)]">{(o.items || []).reduce((s, it) => s + it.quantity, 0)} items</td>
                  <td className="px-4 py-3 font-bold text-[var(--dd-green)]">{formatUSD(o.total)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-3 py-1 cursor-pointer border-0 outline-none capitalize ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-700"}`}
                      data-testid={`order-status-${o.order_number}`}
                    >
                      {[...STATUS_FLOW, "cancelled"].map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--dsf-text-muted)]">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelected(o)} className="text-[var(--dd-green)] hover:text-[var(--dd-mustard)] transition" title="View" data-testid={`order-view-${o.order_number}`}><Eye size={16} /></button>
                      <button onClick={() => openInvoice(o.id)} className="text-[var(--dd-green)] hover:text-[var(--dd-mustard)] transition" title="Invoice" data-testid={`order-invoice-${o.order_number}`}><FileText size={16} /></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-black/5 bg-[var(--dd-bone)]/30">
            <div className="text-xs text-[var(--dsf-text-muted)]">Page {currentPage} / {totalPages}</div>
            <div className="flex gap-2">
              <button onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} disabled={skip === 0} className="btn-pill-outline !py-2 !px-4 disabled:opacity-40" data-testid="orders-prev-page"><ChevronLeft size={14} /> Prev</button>
              <button onClick={() => setSkip(skip + PAGE_SIZE)} disabled={currentPage >= totalPages} className="btn-pill-outline !py-2 !px-4 disabled:opacity-40" data-testid="orders-next-page">Next <ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Order details dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Order {selected?.order_number}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              {(() => { const pb = pickupBadge(selected.delivery_method); return (
                <div className={`rounded-2xl px-5 py-4 flex items-center gap-4 ${pb.cls}`} data-testid="order-detail-pickup-banner">
                  <div className="text-3xl">{pb.emoji}</div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] opacity-90">Lieu de retrait</div>
                    <div className="font-bold text-lg leading-tight">{pb.label === "FOOD TRUCK" ? "Food Truck (mobile)" : "Restaurant — 2923 Walnut Bend Ln"}</div>
                  </div>
                </div>
              ); })()}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs uppercase tracking-wider text-[var(--dsf-text-muted)] mb-1">Customer</div><div className="font-medium">{selected.customer_name}</div><div>{selected.customer_email}</div><div>{selected.customer_phone}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-[var(--dsf-text-muted)] mb-1">Pickup</div><div className="capitalize font-semibold">{selected.delivery_method?.replace("pickup_", "").replace("_", " ")}</div><div className="text-xs text-[var(--dsf-text-muted)] mt-1">Prêt en 15 min</div></div>
              </div>
              <div className="border-t border-black/10 pt-3">
                <div className="text-xs uppercase tracking-wider text-[var(--dsf-text-muted)] mb-2">Items</div>
                {(selected.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between py-1.5 text-sm">
                    <span>{it.name} × {it.quantity}</span>
                    <span className="font-semibold">{formatUSD(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-black/10 pt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatUSD(selected.subtotal || 0)}</span></div>
                <div className="flex justify-between"><span>Delivery</span><span>{formatUSD(selected.delivery_fee || 0)}</span></div>
                {selected.discount > 0 && (<div className="flex justify-between text-[var(--dd-green)]"><span>Discount</span><span>-{formatUSD(selected.discount)}</span></div>)}
                <div className="flex justify-between font-bold text-base text-[var(--dd-green)] pt-2 border-t border-black/10"><span>Total</span><span>{formatUSD(selected.total)}</span></div>
              </div>
              <div className="flex gap-2 pt-3 border-t border-black/10 flex-wrap">
                {(selected.payment_status === "authorized" || selected.status === "awaiting_acceptance") && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/admin/orders/${selected.id}/accept`);
                          toast.success(data.captured ? "Payment captured — order accepted" : data.message);
                          setSelected(null);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Accept failed");
                        }
                      }}
                      className="bg-[var(--dd-green)] text-white px-4 py-2 rounded-full text-sm font-bold hover:opacity-90 flex items-center gap-1.5"
                      data-testid="order-accept-button"
                    >
                      <Check size={14} /> Accept & charge
                    </button>
                    <button
                      onClick={async () => {
                        const reason = window.prompt("Reason for rejection (optional):") || "";
                        if (!window.confirm("Reject this order? Customer's card hold will be released.")) return;
                        try {
                          await api.post(`/admin/orders/${selected.id}/reject`, { reason });
                          toast.success("Order rejected & refunded");
                          setSelected(null);
                          load();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Reject failed");
                        }
                      }}
                      className="bg-[var(--dd-coral)] text-white px-4 py-2 rounded-full text-sm font-bold hover:opacity-90 flex items-center gap-1.5"
                      data-testid="order-reject-button"
                    >
                      <X size={14} /> Reject & refund
                    </button>
                  </>
                )}
                <button onClick={() => openInvoice(selected.id)} className="btn-mustard !py-2"><FileText size={14} /> Invoice / PDF</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
